import { link } from '@src/graphql/http-link'
import { accessToken } from '@utils/access-token'

const messageId = () => Math.random() * 46656

// consumeSSE reads a Server-Sent Events body and dispatches each `data:` frame.
// The backend sends {type:"delta",text}, {type:"done",answer,sources},
// {type:"error",message}, and {type:"status",step} while a tool runs (step is the
// tool's name, or "answer" once the model is writing again). Frames are separated
// by a blank line; a chunk can split a frame, so we buffer until we see the
// separator.
async function consumeSSE(body, { onDelta, onDone, onError, onStatus }) {
  const reader = body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  for (;;) {
    const { value, done } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    let sep
    while ((sep = buffer.indexOf('\n\n')) !== -1) {
      const frame = buffer.slice(0, sep)
      buffer = buffer.slice(sep + 2)
      const dataLine = frame.split('\n').find((l) => l.startsWith('data:'))
      if (!dataLine) continue
      let evt
      try {
        evt = JSON.parse(dataLine.slice(5).trim())
      } catch {
        continue
      }
      if (evt.type === 'delta') onDelta(evt.text || '')
      else if (evt.type === 'done') onDone(evt)
      else if (evt.type === 'error') onError(evt.message)
      else if (evt.type === 'status' && onStatus) onStatus(evt.step || '')
    }
  }
}

// maxHistoryTurns caps how many prior turns we send back to askBowvue, bounding
// payload size / model cost. The most recent turns carry the context a
// follow-up ("yes", "the first one") depends on, so we keep the tail.
const maxHistoryTurns = 12

// buildHistory maps prior conversation turns to the AskTurn shape the API
// expects ({ role: USER|ASSISTANT, content }). Error turns (and any turn without
// text) are dropped. Content is required and an empty turn adds no context.
const buildHistory = (messages) =>
  messages
    .filter((m) => m.text)
    .slice(-maxHistoryTurns)
    .map((m) => ({
      role: m.role === 'assistant' ? 'ASSISTANT' : 'USER',
      content: m.text,
    }))

// The conversation is persisted to localStorage so an accidental refresh doesn't
// lose it. It's restored on load and only cleared when the user ends the chat
// (CLEAR_MESSAGES). `loading` is intentionally not persisted: a refresh mid-
// request should come back idle, not with a stuck spinner.
const STORAGE_KEY = 'chat.state'

function getSavedState() {
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY)) || {}
  } catch {
    return {}
  }
}

function persist(state) {
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        open: state.open,
        messages: state.messages,
        scopedLocationId: state.scopedLocationId,
      })
    )
  } catch {
    // Storage unavailable/full (private mode, quota). Degrade to in-memory only.
  }
}

function clearSavedState() {
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Storage unavailable. Nothing to clear.
  }
}

const saved = getSavedState()

export const state = {
  open: saved.open || false,
  messages: saved.messages || [],
  loading: false,
  scopedLocationId: saved.scopedLocationId ?? null,
  // Whether THIS user may use askBowvue. Owned by the layout, which runs the one
  // askBowvueAvailable query and mirrors the answer here so the dashboard's ask bar
  // can gate on it too, two entry points, still one request.
  //
  // Deliberately NOT persisted and defaulted to false: entitlement can be revoked by
  // a backend deploy, and a stale `true` from localStorage would render a control that
  // refuses every question. Not the security boundary either way, askBowvue enforces
  // that itself.
  available: false,
}

export const getters = {
  isOpen(state) {
    return state.open
  },
  messages(state) {
    return state.messages
  },
  isLoading(state) {
    return state.loading
  },
  isAvailable(state) {
    return state.available
  },
}

export const mutations = {
  SET_AVAILABLE(state, value) {
    state.available = !!value
  },
  SET_OPEN(state, open) {
    state.open = open
    persist(state)
  },
  // loading is not persisted, so this mutation doesn't touch storage.
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  ADD_MESSAGE(state, message) {
    state.messages = [...state.messages, message]
    persist(state)
  },
  // Adds the assistant's in-progress message WITHOUT persisting: it starts empty
  // and grows via APPEND_TO_MESSAGE, and a refresh mid-stream should not restore a
  // stuck, empty streaming bubble. It's persisted once it finishes (FINISH_MESSAGE).
  START_ASSISTANT_MESSAGE(state, { id }) {
    state.messages = [
      ...state.messages,
      { id, role: 'assistant', text: '', streaming: true },
    ]
  },
  // Appends a streamed token. Deliberately does NOT persist. Writing localStorage
  // on every token would be wasteful; FINISH_MESSAGE persists the completed answer.
  APPEND_TO_MESSAGE(state, { id, text }) {
    const msg = state.messages.find((m) => m.id === id)
    if (msg) msg.text += text
  },
  // What the answer is doing right now (a tool's name, or "answer"), for the
  // bubble's status line. Not persisted: it only means anything mid-stream.
  SET_MESSAGE_STEP(state, { id, step }) {
    const msg = state.messages.find((m) => m.id === id)
    if (msg) msg.step = step
  },
  // Replaces the streamed text with the server's authoritative answer, attaches
  // sources, and clears the streaming flag. Using the authoritative text also
  // covers answers that produced no deltas (e.g. "no matching documents").
  FINISH_MESSAGE(state, { id, answer, sources }) {
    const msg = state.messages.find((m) => m.id === id)
    if (msg) {
      msg.text = answer
      msg.sources = sources
      msg.streaming = false
      msg.step = null
    }
    state.messages = [...state.messages]
    persist(state)
  },
  // Turns the in-progress message into an error bubble.
  FAIL_MESSAGE(state, { id, error }) {
    const msg = state.messages.find((m) => m.id === id)
    if (msg) {
      msg.error = error
      msg.streaming = false
      msg.step = null
    }
    state.messages = [...state.messages]
    persist(state)
  },
  // Ending the chat is the only thing that clears the stored conversation.
  CLEAR_MESSAGES(state) {
    state.messages = []
    persist(state)
  },
  SET_SCOPED_LOCATION(state, locationId) {
    state.scopedLocationId = locationId
    persist(state)
  },
  // Full wipe (used on logout): drop the conversation, location scope, and the
  // persisted copy so the next user on this browser starts clean.
  RESET(state) {
    state.open = false
    state.messages = []
    state.loading = false
    state.scopedLocationId = null
    clearSavedState()
  },
}

export const actions = {
  setAvailable({ commit }, value) {
    commit('SET_AVAILABLE', value)
  },
  openPanel({ commit }) {
    commit('SET_OPEN', true)
  },
  closePanel({ commit }) {
    commit('SET_OPEN', false)
  },
  endChat({ commit }) {
    commit('CLEAR_MESSAGES')
    commit('SET_OPEN', false)
  },
  // Clears everything, including the persisted copy. Dispatched on logout so
  // one user's chat never carries over to the next.
  resetChat({ commit }) {
    commit('RESET')
  },
  setScopedLocation({ commit, state }, locationId) {
    if (locationId === state.scopedLocationId) return
    commit('SET_SCOPED_LOCATION', locationId)
  },
  // Ask from outside the panel (the dashboard ask bar): open first, then send, so
  // the user sees their question land and the loading state rather than typing into a
  // bar that appears to do nothing while the request is in flight.
  async askFromBar({ dispatch }, question) {
    const text = (question || '').trim()
    if (!text) return
    dispatch('openPanel')
    await dispatch('askQuestion', text)
  },
  async askQuestion({ commit, state, rootState }, question) {
    // Snapshot the conversation before adding the new question, so history holds
    // only the prior turns. The current question is sent separately.
    const history = buildHistory(state.messages)
    commit('ADD_MESSAGE', {
      id: messageId(),
      role: 'user',
      text: question,
    })
    // The assistant bubble is created up front and grows as tokens stream in.
    const assistantId = messageId()
    commit('START_ASSISTANT_MESSAGE', { id: assistantId })
    commit('SET_LOADING', true)
    try {
      // Streamed over SSE (POST /ask-bowvue/stream) rather than the GraphQL query,
      // so the answer renders token-by-token instead of after a long blank wait.
      const token = await accessToken(
        rootState.auth?.currentUser?.accessToken?.jwtToken
      )
      const res = await fetch(`${link.bowvue_base_uri}/ask-bowvue/stream`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          ...(token ? { authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          question,
          locationId: state.scopedLocationId,
          history,
        }),
      })
      if (!res.ok || !res.body) {
        throw new Error(`Request failed (${res.status})`)
      }

      let failed = false
      await consumeSSE(res.body, {
        onDelta: (text) =>
          commit('APPEND_TO_MESSAGE', { id: assistantId, text }),
        onStatus: (step) =>
          commit('SET_MESSAGE_STEP', { id: assistantId, step }),
        onDone: ({ answer, sources }) =>
          commit('FINISH_MESSAGE', { id: assistantId, answer, sources }),
        onError: (message) => {
          failed = true
          commit('FAIL_MESSAGE', {
            id: assistantId,
            error: message || 'Something went wrong. Please try again.',
          })
        },
      })

      // A stream that ends without a done or error frame (dropped connection) still
      // needs to leave the bubble in a terminal state rather than stuck streaming.
      const msg = state.messages.find((m) => m.id === assistantId)
      if (!failed && msg && msg.streaming) {
        if (msg.text) {
          commit('FINISH_MESSAGE', {
            id: assistantId,
            answer: msg.text,
            sources: msg.sources || [],
          })
        } else {
          commit('FAIL_MESSAGE', {
            id: assistantId,
            error:
              'The connection dropped before an answer arrived. Please try again.',
          })
        }
      }
    } catch (err) {
      commit('FAIL_MESSAGE', {
        id: assistantId,
        error: err.message || 'Something went wrong. Please try again.',
      })
    } finally {
      commit('SET_LOADING', false)
    }
  },
}
